import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Database, CheckCircle, AlertTriangle, XCircle, BarChart3, Shield, Copy, TrendingUp, ShieldCheck, RefreshCw, GitMerge, Banknote } from 'lucide-react';
import { BookCleanupSection } from '@/components/BookCleanup';

const HEALTH_COLORS = { good: '#1D9E75', warning: '#E8A020', critical: '#E24B4A' };
const getHealthLevel = (score) => score >= 75 ? 'good' : score >= 50 ? 'warning' : 'critical';

function FieldBar({ label, pct, count, missing }) {
  const color = pct >= 80 ? '#1D9E75' : pct >= 50 ? '#E8A020' : '#E24B4A';
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-36 text-[11px] truncate" style={{ color: '#0D1B2A' }}>{label}</span>
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.12)' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-12 text-right text-[11px] font-mono font-semibold" style={{ color }}>{pct}%</span>
      <span className="w-16 text-right text-[10px]" style={{ color: '#5C6B7A' }}>{count.toLocaleString()}</span>
    </div>
  );
}

function ValidationSection({ api }) {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [merging, setMerging] = useState(null);
  const [ops, setOps] = useState([]);
  const [mergeRun, setMergeRun] = useState(null);
  const loadOps = () => api.get('/data-quality/golden/merge-ops').then(r => setOps(r.data.ops || [])).catch(() => {});
  const loadRun = () => api.get('/data-quality/golden/merge-run').then(r => setMergeRun(r.data)).catch(() => {});
  useEffect(() => { api.get('/data-quality/validation').then(r => setV(r.data)).catch(() => {}); loadOps(); loadRun(); }, [api]);
  useEffect(() => {
    if (mergeRun?.status !== 'running') return;
    const t = setInterval(() => {
      api.get('/data-quality/golden/merge-run').then(r => {
        setMergeRun(r.data);
        if (r.data.status === 'complete') { loadOps(); api.post('/data-quality/validate').then(res => setV(res.data)).catch(() => {}); }
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [mergeRun?.status, api]);
  const run = () => {
    setBusy(true);
    api.post('/data-quality/validate').then(r => setV(r.data)).catch(() => {}).finally(() => setBusy(false));
  };
  const mergeCluster = (abn) => {
    setMerging(abn);
    api.post('/data-quality/golden/merge', { abn })
      .then(() => Promise.all([api.post('/data-quality/validate').then(r => setV(r.data)), loadOps()]))
      .catch(() => {}).finally(() => setMerging(null));
  };
  const mergeAll = () => {
    setMerging('all');
    api.post('/data-quality/golden/merge-all')
      .then(() => Promise.all([api.post('/data-quality/validate').then(r => setV(r.data)), loadOps()]))
      .catch(() => {}).finally(() => setMerging(null));
  };
  const mergeAllFull = () => {
    setMerging('full');
    api.post('/data-quality/golden/merge-all-full')
      .then(() => loadRun())
      .catch(() => {}).finally(() => setMerging(null));
  };
  const unmerge = (opId) => {
    setMerging(opId);
    api.post('/data-quality/golden/unmerge', { op_id: opId })
      .then(() => Promise.all([api.post('/data-quality/validate').then(r => setV(r.data)), loadOps()]))
      .catch(() => {}).finally(() => setMerging(null));
  };
  const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
  const hasRun = v && v.rules;
  return (
    <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="validation-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" style={{ color: C.blue }} />
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Validation & Golden Records</h3>
        </div>
        <button onClick={run} disabled={busy} data-testid="run-validation-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-60" style={{ background: C.blue }}>
          {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}{busy ? 'Validating…' : 'Run Validation'}
        </button>
      </div>
      {!hasRun ? (
        <p className="text-sm" style={{ color: C.slate }}>Run the accuracy validation engine to check ABN checksums, postcode↔state consistency, formats, bounds and critical fields — and resolve golden records among duplicates.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs mb-4" style={{ color: C.slate }}>
            <span>Accuracy <b style={{ color: v.accuracy_pct >= 95 ? C.green : v.accuracy_pct >= 85 ? C.amber : C.red }} data-testid="accuracy-pct">{v.accuracy_pct}%</b></span>
            <span>Sample <b style={{ color: C.navy }}>{v.sample_size?.toLocaleString()}</b> / {v.entities_total?.toLocaleString()}</span>
            <span>Violations <b style={{ color: v.total_violations ? C.amber : C.green }}>{v.total_violations?.toLocaleString()}</b></span>
          </div>
          <div className="space-y-2 mb-5">
            {Object.entries(v.rules).map(([key, r]) => (
              <div key={key} className="flex items-center gap-3" data-testid={`rule-${key}`}>
                <span className="w-52 text-[11px] truncate" style={{ color: C.navy }}>{r.label}</span>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.12)' }}>
                  <div className="h-full rounded-full" style={{ width: `${r.pass_pct}%`, background: r.pass_pct >= 95 ? C.green : r.pass_pct >= 85 ? C.amber : C.red }} />
                </div>
                <span className="w-12 text-right text-[11px] font-mono font-semibold" style={{ color: r.pass_pct >= 95 ? C.green : r.pass_pct >= 85 ? C.amber : C.red }}>{r.pass_pct}%</span>
                <span className="w-20 text-right text-[10px]" style={{ color: C.slate }}>{r.failed.toLocaleString()} fail</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <GitMerge className="w-4 h-4" style={{ color: C.blue }} />
              <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Golden Records</h4>
            </div>
            <div className="flex items-center gap-2">
              {v.golden_records?.clusters?.length > 0 && (
                <button onClick={mergeAll} disabled={!!merging || mergeRun?.status === 'running'} data-testid="merge-all-btn"
                  className="flex items-center gap-1.5 px-3 py-1 rounded text-xs font-semibold text-white disabled:opacity-60" style={{ background: C.amber }}>
                  <GitMerge className="w-3.5 h-3.5" />{merging === 'all' ? 'Merging…' : 'Merge Top 25'}
                </button>
              )}
              <button onClick={mergeAllFull} disabled={!!merging || mergeRun?.status === 'running' || !v.golden_records?.records_to_merge} data-testid="merge-all-full-btn"
                className="flex items-center gap-1.5 px-3 py-1 rounded text-xs font-semibold text-white disabled:opacity-60" style={{ background: v.golden_records?.records_to_merge ? C.blue : C.green }}>
                <GitMerge className="w-3.5 h-3.5" />
                {mergeRun?.status === 'running' ? 'Auto-Merging…' : v.golden_records?.records_to_merge ? 'Auto-Merge Full Book' : 'Book Clean — No Duplicates'}
              </button>
            </div>
          </div>
          {mergeRun && mergeRun.status !== 'never_run' && (
            <div className="mb-3 px-3 py-2 rounded text-xs flex flex-wrap items-center gap-x-5 gap-y-1"
              style={{ background: mergeRun.status === 'running' ? 'rgba(26,76,124,0.06)' : 'rgba(29,158,117,0.07)' }}
              data-testid="merge-run-status">
              {mergeRun.status === 'running' && <RefreshCw className="w-3.5 h-3.5 animate-spin" style={{ color: C.blue }} />}
              <span style={{ color: C.navy }}>Full-book merge <b style={{ color: mergeRun.status === 'running' ? C.blue : C.green }}>{mergeRun.status === 'running' ? 'RUNNING' : 'COMPLETE'}</b></span>
              <span style={{ color: C.slate }}>Clusters <b style={{ color: C.navy }}>{mergeRun.clusters_merged ?? 0}</b> / {mergeRun.clusters_total ?? 0}</span>
              <span style={{ color: C.slate }}>Entities merged <b style={{ color: C.green }}>{(mergeRun.records_merged ?? 0).toLocaleString()}</b></span>
              {mergeRun.errors > 0 && <span style={{ color: C.red }}>{mergeRun.errors} errors</span>}
              {mergeRun.finished_at && <span style={{ color: C.slate }}>{new Date(mergeRun.finished_at).toLocaleString('en-AU')}</span>}
            </div>
          )}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs mb-3" style={{ color: C.slate }}>
            <span>Duplicate clusters <b style={{ color: C.navy }}>{v.golden_records?.duplicate_clusters}</b></span>
            <span>Records to merge <b style={{ color: v.golden_records?.records_to_merge ? C.amber : C.green }}>{v.golden_records?.records_to_merge}</b></span>
          </div>
          {v.golden_records?.clusters?.length > 0 && (
            <div className="rounded border overflow-hidden" style={{ borderColor: 'rgba(136,150,168,0.14)' }}>
              <div className="grid grid-cols-12 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ background: '#F4F6F9', color: C.slate }}>
                <span className="col-span-3">ABN</span><span className="col-span-4">Golden Record</span><span className="col-span-2 text-right">Complete</span><span className="col-span-1 text-right">Dup</span><span className="col-span-2 text-right">Action</span>
              </div>
              <div className="max-h-56 overflow-y-auto">
                {v.golden_records.clusters.slice(0, 12).map((c, i) => (
                  <div key={i} className="grid grid-cols-12 px-3 py-1.5 text-xs border-t items-center" style={{ borderColor: 'rgba(136,150,168,0.07)' }} data-testid={`golden-${i}`}>
                    <span className="col-span-3 font-mono truncate" style={{ color: C.slate }}>{c.abn}</span>
                    <span className="col-span-4 truncate" style={{ color: C.navy }}>{c.golden?.legal_name || '—'}</span>
                    <span className="col-span-2 text-right font-mono" style={{ color: C.green }}>{c.golden_completeness != null ? `${c.golden_completeness}%` : '—'}</span>
                    <span className="col-span-1 text-right font-mono" style={{ color: C.amber }}>{c.duplicates_to_merge?.length}</span>
                    <span className="col-span-2 text-right">
                      <button onClick={() => mergeCluster(c.abn)} disabled={!!merging} data-testid={`merge-${i}`}
                        className="text-[11px] font-semibold px-2 py-0.5 rounded disabled:opacity-50" style={{ color: C.blue, border: `1px solid ${C.blue}44` }}>
                        {merging === c.abn ? '…' : 'Merge'}
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {ops.length > 0 && (
            <div className="mt-4" data-testid="merge-ops-list">
              <h4 className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.navy }}>Recent Merges (reversible)</h4>
              <div className="space-y-1">
                {ops.slice(0, 8).map(op => (
                  <div key={op.op_id} className="flex items-center justify-between text-xs px-2 py-1 rounded" style={{ background: '#F4F6F9' }} data-testid={`op-${op.op_id}`}>
                    <span style={{ color: C.navy }} className="truncate">{op.golden_name || op.golden_id} <span style={{ color: C.slate }}>· {op.merged_ids?.length} merged · {op.abn}</span></span>
                    <button onClick={() => unmerge(op.op_id)} disabled={!!merging} data-testid={`unmerge-${op.op_id}`}
                      className="text-[11px] font-semibold px-2 py-0.5 rounded disabled:opacity-50 shrink-0" style={{ color: C.red, border: `1px solid ${C.red}44` }}>
                      {merging === op.op_id ? '…' : 'Undo'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function IntegritySection({ api }) {
  const [audit, setAudit] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [repairing, setRepairing] = useState(false);
  useEffect(() => {
    api.get('/data-quality/integrity').then(r => setAudit(r.data)).catch(() => {});
    api.get('/data-quality/integrity/history?days=30').then(r => setHistory(r.data)).catch(() => {});
  }, [api]);
  const refreshHistory = () => api.get('/data-quality/integrity/history?days=30').then(r => setHistory(r.data)).catch(() => {});
  const run = () => {
    setBusy(true);
    api.get('/data-quality/integrity').then(r => { setAudit(r.data); refreshHistory(); }).catch(() => {}).finally(() => setBusy(false));
  };
  const repair = () => {
    setRepairing(true);
    api.post('/data-quality/integrity/repair')
      .then(() => api.get('/data-quality/integrity').then(r => { setAudit(r.data); refreshHistory(); }))
      .catch(() => {}).finally(() => setRepairing(false));
  };
  if (!audit) return null;
  const clean = audit.clean;
  return (
    <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="integrity-audit-section">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <ShieldCheck className="w-4 h-4" style={{ color: clean ? '#1D9E75' : '#E24B4A' }} />
        <h3 className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Insurance Integrity Audit</h3>
        <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid="integrity-status"
          style={{ background: clean ? '#E7F6EF' : '#FDF1F1', color: clean ? '#1D9E75' : '#E24B4A' }}>
          {clean ? `Clean — 0 violations` : `${audit.total_violations.toLocaleString()} violations`}
        </span>
        <span className="text-[10px]" style={{ color: '#5C6B7A' }}>
          {audit.rules_checked} reality rules · {audit.totals.policies.toLocaleString()} policies · {audit.totals.claims.toLocaleString()} claims · {audit.totals.renewals.toLocaleString()} renewals
        </span>
        <div className="ml-auto flex gap-2">
          <button onClick={run} disabled={busy} data-testid="integrity-run-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-semibold border transition-colors hover:bg-slate-50 disabled:opacity-50"
            style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#1A4C7C' }}>
            <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Re-run Audit
          </button>
          {!clean && (
            <button onClick={repair} disabled={repairing} data-testid="integrity-repair-btn"
              className="px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white disabled:opacity-50"
              style={{ background: '#E24B4A' }}>
              {repairing ? 'Repairing…' : 'Auto-Repair All'}
            </button>
          )}
        </div>
      </div>
      <p className="text-[11px] mb-3" style={{ color: '#5C6B7A' }}>
        Every record is checked against insurance reality: dates of loss precede reporting and settlement, claims fall within
        the policy period, finalised claims release reserves, open claims carry reserves, terms and premiums are sane.
        ARIA re-runs this sweep nightly and auto-repairs drift.
      </p>
      {!clean && (
        <div className="space-y-1.5" data-testid="integrity-violations-list">
          {audit.rules.map(r => (
            <div key={r.code} className="flex items-center gap-3 text-[11px] py-1 px-2 rounded-sm" style={{ background: '#FDF7EC' }}>
              <span className="font-mono font-bold w-14" style={{ color: '#E8A020' }}>{r.code}</span>
              <span style={{ color: '#0D1B2A' }}>{r.description}</span>
              <span className="ml-auto font-mono font-bold" style={{ color: '#E24B4A' }}>{r.count.toLocaleString()}</span>
              <span className="text-[10px]" style={{ color: '#5C6B7A' }}>e.g. {r.samples.slice(0, 2).join(', ')}</span>
            </div>
          ))}
        </div>
      )}
      {history?.days?.length > 0 && <IntegrityTrend history={history} />}
    </Card>
  );
}

function IntegrityTrend({ history }) {
  const s = history.summary;
  const data = history.days.map(d => ({
    day: d.date.slice(5),
    repaired: d.auto_repaired || 0,
    clean: d.end_clean,
  }));
  const maxV = Math.max(...data.map(d => d.repaired), 1);
  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(136,150,168,0.12)' }} data-testid="integrity-trend-card">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>30-Day Data Discipline</h4>
        {[
          { label: 'Clean streak', value: `${s.clean_streak}d`, color: '#1D9E75', testid: 'integrity-clean-streak' },
          { label: 'Clean days', value: `${s.clean_days}/${s.days_audited}`, color: '#1A4C7C', testid: 'integrity-clean-days' },
          { label: 'Drift found', value: s.total_found.toLocaleString(), color: '#E8A020', testid: 'integrity-total-found' },
          { label: 'Auto-repaired', value: s.total_repaired.toLocaleString(), color: '#1D9E75', testid: 'integrity-total-repaired' },
        ].map(k => (
          <span key={k.label} className="text-[11px]" style={{ color: '#5C6B7A' }} data-testid={k.testid}>
            {k.label} <strong className="font-mono" style={{ color: k.color }}>{k.value}</strong>
          </span>
        ))}
      </div>
      <div className="flex items-end gap-[3px] h-16" data-testid="integrity-trend-bars">
        {data.map((d, i) => (
          <div key={i} className="flex-1 h-full flex flex-col items-center justify-end group relative" title={`${d.day}: ${d.repaired.toLocaleString()} repaired · ${d.clean ? 'clean' : 'violations open'}`}>
            <div className="w-full rounded-t-sm transition-opacity hover:opacity-70"
              style={{ height: `${Math.max((Math.log1p(d.repaired) / Math.log1p(maxV)) * 100, 4)}%`, background: d.clean ? '#1D9E75' : '#E24B4A', opacity: 0.85 }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[9px] mt-1" style={{ color: '#64748B' }}>
        <span>{data[0]?.day}</span>
        <span>Bar height = drift auto-repaired that day (log scale) · green = day ended clean</span>
        <span>{data[data.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function PremiumRepairSection({ api }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/data-quality/premium-repair?limit=50').then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [api]);

  const record = async (policy_id) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/data-quality/premium-repair/${policy_id}`,
        { annual_premium: Number(amount), reason });
      toast.success(`${res.policy_number}: premium of record set to $${Number(res.to).toLocaleString()}`);
      if (res.insurance_class_set) toast.message(`Class also recorded as ${res.insurance_class_set}`);
      setOpenId(''); setAmount(''); setReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The premium could not be recorded');
    } finally { setBusy(false); }
  };

  const voidPolicy = async (policy_id) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/data-quality/premium-repair/${policy_id}/void`, { reason });
      toast.success(`${res.policy_number}: off risk from inception (${res.effective_date?.slice(0, 10)})`);
      if (res.invoices_cancelled) toast.message(`${res.invoices_cancelled} invoice(s) cancelled with it`);
      setOpenId(''); setAmount(''); setReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The policy could not be voided');
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const clean = !data.total;
  return (
    <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="premium-repair-section">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <Banknote className="w-4 h-4" style={{ color: clean ? '#1D9E75' : '#E24B4A' }} />
        <h3 className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Premium Repair</h3>
        <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" data-testid="premium-repair-count"
          style={{ background: clean ? '#E7F6EF' : '#FDF1F1', color: clean ? '#1D9E75' : '#E24B4A' }}>
          {clean ? 'none — every live policy carries a premium' : `${data.total.toLocaleString()} live polic${data.total === 1 ? 'y' : 'ies'} with no premium`}
        </span>
      </div>
      <p className="text-[11px] mb-3" style={{ color: '#5C6B7A' }}>{data.note}</p>
      {clean ? (
        <p className="text-[11px]" style={{ color: '#5C6B7A' }} data-testid="premium-repair-empty">
          Nothing to repair. Rule PO-03 has no open findings.
        </p>
      ) : (
        <table className="w-full text-[11px]">
          <thead><tr className="text-left text-[9px] uppercase" style={{ color: '#5C6B7A' }}>
            <th className="py-1 pr-2">Policy</th><th className="py-1 pr-2">Insured</th><th className="py-1 pr-2">Class</th>
            <th className="py-1 pr-2">Sum insured</th><th className="py-1 pr-2">Reference premium</th>
            <th className="py-1 pr-2">How it happened</th><th className="py-1"></th>
          </tr></thead>
          <tbody>
            {data.policies.map(p => (
              <React.Fragment key={p.policy_id}>
                <tr className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`premium-repair-row-${p.policy_id}`}>
                  <td className="py-1.5 pr-2 font-mono" style={{ color: '#0D1B2A' }}>{p.policy_number}</td>
                  <td className="py-1.5 pr-2" style={{ color: '#0D1B2A' }}>{p.business_name}</td>
                  <td className="py-1.5 pr-2" style={{ color: '#5C6B7A' }}>
                    {p.class || '—'}
                    {!p.insurance_class && (
                      <span className="ml-1 px-1 rounded text-[9px] font-bold uppercase" style={{ background: '#FDF7EC', color: '#E8A020' }}
                        data-testid={`premium-repair-noclass-${p.policy_id}`}>no class key</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2 font-mono" style={{ color: '#0D1B2A' }}>${(p.sum_insured || 0).toLocaleString()}</td>
                  <td className="py-1.5 pr-2 font-mono" style={{ color: p.reference?.reference_premium ? '#1A4C7C' : '#5C6B7A' }}>
                    {p.reference?.reference_premium
                      ? `$${p.reference.reference_premium.toLocaleString()} · ${p.reference.comparables} comps`
                      : `no reference (${p.reference?.comparables || 0} comps)`}
                  </td>
                  <td className="py-1.5 pr-2 text-[10px]" style={{ color: '#5C6B7A' }}>{p.how_it_happened}</td>
                  <td className="py-1.5">
                    <button onClick={() => {
                      const next = openId === p.policy_id ? '' : p.policy_id;
                      setOpenId(next);
                      setAmount(next && p.reference?.reference_premium ? String(p.reference.reference_premium) : '');
                      setReason('');
                    }} className="px-2 py-1 rounded-sm text-[10px] font-semibold border hover:bg-slate-50"
                      style={{ borderColor: 'rgba(136,150,168,0.3)', color: '#1A4C7C' }}
                      data-testid={`premium-repair-open-${p.policy_id}`}>Record premium</button>
                  </td>
                </tr>
                {openId === p.policy_id && (
                  <tr data-testid={`premium-repair-form-${p.policy_id}`}>
                    <td colSpan={7} className="p-2" style={{ background: 'rgba(26,76,124,0.04)' }}>
                      <div className="flex flex-wrap gap-2 items-center">
                        <Input value={amount} onChange={e => setAmount(e.target.value)} type="number" placeholder="Annual premium"
                          className="w-36 h-7 text-[11px] rounded-sm" data-testid="premium-repair-amount" />
                        <Input value={reason} onChange={e => setReason(e.target.value)}
                          placeholder="How was this premium arrived at? (recorded on the policy, min 10 chars)"
                          className="flex-1 min-w-[260px] h-7 text-[11px] rounded-sm" data-testid="premium-repair-reason" />
                        <button onClick={() => record(p.policy_id)}
                          disabled={busy || reason.trim().length < 10 || !(Number(amount) > 0)}
                          className="px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white disabled:opacity-50"
                          style={{ background: '#1A4C7C' }} data-testid={`premium-repair-confirm-${p.policy_id}`}>
                          {busy ? 'Recording…' : 'Record as premium of record'}
                        </button>
                        <button onClick={() => voidPolicy(p.policy_id)}
                          disabled={busy || reason.trim().length < 10}
                          className="px-3 py-1.5 rounded-sm text-[11px] font-semibold border disabled:opacity-50"
                          style={{ borderColor: '#E24B4A', color: '#E24B4A' }}
                          data-testid={`premium-repair-void-${p.policy_id}`}>
                          {busy ? 'Working…' : 'Void from inception'}
                        </button>
                      </div>
                      <p className="text-[10px] mt-1" style={{ color: '#5C6B7A' }}>
                        A policy that bound with no premium never had consideration — voiding takes it off risk from
                        inception with no return premium and no s59 notice. Refused if a claim was made against it.
                      </p>
                      <p className="text-[10px] mt-1" style={{ color: '#5C6B7A' }}>
                        {p.reference?.basis}
                        {p.reference?.range_low ? ` · comparable range $${p.reference.range_low.toLocaleString()}–$${p.reference.range_high.toLocaleString()}` : ''}
                        {` · ${p.invoices} invoice(s), ${p.claims} claim(s) on this policy`}
                      </p>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export default function DataQualityPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadReport = (refresh) => api.get(`/data-quality${refresh ? '?refresh=true' : ''}`).then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); });
  useEffect(() => {
    loadReport(false).finally(() => setLoading(false));
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshReport = async () => { setRefreshing(true); await loadReport(true); setRefreshing(false); };

  if (loading) return <div data-testid="dq-loading" className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-sm" />)}</div>;

  const s = data?.summary || {};
  const healthLevel = getHealthLevel(s.health_score);

  return (
    <div className="space-y-6" data-testid="data-quality-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans'" }}>Data Quality</h1>
          <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>Entity database health, completeness, and duplicate detection</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }} data-testid="dq-report-as-at">
            Register scan as at {data?.computed_at ? new Date(data.computed_at).toLocaleTimeString() : '—'}{data?.cached ? ' (materialised)' : ''}
          </p>
          <button type="button" onClick={refreshReport} disabled={refreshing} data-testid="dq-report-refresh-btn"
                  className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-sm border border-[rgba(136,150,168,0.3)] bg-white hover:bg-[#F4F6F8] disabled:opacity-50"
                  style={{ color: '#1A4C7C' }}>
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Scanning 300k rows…' : 'Rescan now'}
          </button>
        </div>
      </div>

      <IntegritySection api={api} />

      <PremiumRepairSection api={api} />

      <BookCleanupSection api={api} />

      {/* Health Score + KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        <Card className="p-4 rounded-sm border-2 bg-white col-span-2 md:col-span-1" style={{ borderColor: HEALTH_COLORS[healthLevel] }} data-testid="health-score">
          <p className="text-[10px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Health Score</p>
          <p className="text-3xl font-bold font-mono mt-1" style={{ color: HEALTH_COLORS[healthLevel] }}>{s.health_score}</p>
          <p className="text-[10px] uppercase font-bold mt-1" style={{ color: HEALTH_COLORS[healthLevel] }}>{healthLevel}</p>
        </Card>
        {[
          { label: 'Register (ABR/Tiger)', value: s.total_entities?.toLocaleString(), icon: Database, color: '#1A4C7C', testid: 'dq-kpi-register' },
          { label: 'On the book', value: s.book_entities?.toLocaleString(), icon: ShieldCheck, color: '#1D9E75', testid: 'dq-kpi-book', hint: 'has a policy, submission, claim, renewal or broker link' },
          { label: 'Prospects', value: s.prospect_entities?.toLocaleString(), icon: CheckCircle, color: '#5C6B7A', testid: 'dq-kpi-prospect' },
          { label: 'Completeness', value: `${s.overall_completeness}%`, icon: BarChart3, color: '#00B4D8' },
          { label: 'Dup ABNs', value: s.duplicate_abns, icon: Copy, color: s.duplicate_abns > 0 ? '#E8A020' : '#1D9E75' },
          { label: 'Locations', value: s.total_locations, icon: TrendingUp, color: '#1A4C7C' },
        ].map((kpi, i) => (
          <Card key={kpi.label} className="p-3 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid={kpi.testid} title={kpi.hint}>
            <div className="flex items-center gap-1.5 mb-1">
              <kpi.icon className="w-3 h-3" style={{ color: kpi.color }} />
              <span className="text-[9px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{kpi.label}</span>
            </div>
            <p className="text-lg font-bold font-mono" style={{ color: '#0D1B2A' }}>{kpi.value}</p>
          </Card>
        ))}
      </div>

      {/* Group Scores */}
      <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="group-scores">
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#0D1B2A' }}>Schema Group Completeness</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(data?.group_scores || {}).map(([key, group]) => {
            const color = group.score >= 80 ? '#1D9E75' : group.score >= 50 ? '#E8A020' : '#E24B4A';
            return (
              <div key={key} className="p-3 rounded-sm" style={{ background: '#F5F7FA' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold" style={{ color: '#0D1B2A' }}>{group.label}</span>
                  <span className="text-sm font-bold font-mono" style={{ color }}>{group.score}%</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
                  <div className="h-full rounded-full" style={{ width: `${group.score}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Field Completeness */}
        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="field-completeness">
          <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Field Completeness</h3>
          <div className="space-y-0">
            {Object.entries(data?.field_completeness || {})
              .sort((a, b) => a[1].pct - b[1].pct)
              .map(([field, info]) => (
                <FieldBar key={field} label={info.label} pct={info.pct} count={info.count} missing={info.missing} />
              ))}
          </div>
        </Card>

        {/* Batch Stats + Distributions */}
        <div className="space-y-4">
          {/* Batch Stats */}
          <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="batch-stats">
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Batch Ingestion Stats</h3>
            <div className="space-y-2">
              {(data?.batch_stats || []).map((batch, i) => (
                <div key={batch.batch} className="flex items-center justify-between p-2 rounded-sm hover:bg-[#F5F7FA]">
                  <div>
                    <span className="text-xs font-mono font-semibold" style={{ color: '#1A4C7C' }}>{batch.batch}</span>
                    <span className="text-[10px] ml-2" style={{ color: '#5C6B7A' }}>{batch.count.toLocaleString()} entities</span>
                  </div>
                  <div className="flex gap-3 text-[10px]">
                    <span style={{ color: '#5C6B7A' }}>Completeness: <strong style={{ color: '#0D1B2A' }}>{batch.avg_completeness}%</strong></span>
                    <span style={{ color: '#5C6B7A' }}>Email: <strong>{batch.email_pct}%</strong></span>
                    <span style={{ color: '#5C6B7A' }}>Claims: <strong style={{ color: batch.claims_pct > 15 ? '#E8A020' : '#0D1B2A' }}>{batch.claims_pct}%</strong></span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Completeness Distribution */}
          <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="completeness-dist">
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Completeness Distribution</h3>
            <div className="space-y-2">
              {(data?.completeness_distribution || []).map((bucket, i) => {
                const maxCount = Math.max(...(data?.completeness_distribution || []).map(b => b.count));
                const pct = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
                return (
                  <div key={bucket.range} className="flex items-center gap-3">
                    <span className="w-16 text-xs font-mono" style={{ color: '#5C6B7A' }}>{bucket.range}</span>
                    <div className="flex-1 h-4 rounded-sm overflow-hidden" style={{ background: 'rgba(136,150,168,0.1)' }}>
                      <div className="h-full rounded-sm" style={{ width: `${pct}%`, background: '#1A4C7C' }} />
                    </div>
                    <span className="w-16 text-right text-xs font-mono" style={{ color: '#0D1B2A' }}>{bucket.count.toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Duplicates */}
          {(data?.duplicate_abns?.length > 0 || data?.duplicate_names?.length > 0) && (
            <Card className="p-5 rounded-sm border-l-4 bg-white" style={{ borderLeftColor: '#E8A020' }} data-testid="duplicates">
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#E8A020' }}>
                <AlertTriangle className="w-4 h-4 inline mr-1" /> Duplicates Detected
              </h3>
              {data.duplicate_abns?.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#5C6B7A' }}>Duplicate ABNs</p>
                  {data.duplicate_abns.slice(0, 5).map((d, i) => (
                    <p key={d.abn} className="text-xs font-mono" style={{ color: '#0D1B2A' }}>{d.abn} <span style={{ color: '#E8A020' }}>({d.count}x)</span></p>
                  ))}
                </div>
              )}
              {data.duplicate_names?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#5C6B7A' }}>Duplicate Names</p>
                  {data.duplicate_names.slice(0, 5).map((d, i) => (
                    <p key={d.name} className="text-xs" style={{ color: '#0D1B2A' }}>{d.name} <span style={{ color: '#E8A020' }}>({d.count}x)</span></p>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Location + Industry Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="location-dist">
          <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Location Distribution</h3>
          {(data?.location_distribution || []).slice(0, 10).map((loc, i) => {
            const maxCount = (data?.location_distribution?.[0]?.count) || 1;
            return (
              <div key={loc.city} className="flex items-center gap-2 py-1">
                <span className="w-40 text-xs truncate" style={{ color: '#0D1B2A' }}>{loc.city}</span>
                <span className="text-[10px] w-6" style={{ color: '#5C6B7A' }}>{loc.country}</span>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.1)' }}>
                  <div className="h-full rounded-full" style={{ width: `${(loc.count/maxCount)*100}%`, background: loc.country === 'NZ' ? '#00B4D8' : '#1A4C7C' }} />
                </div>
                <span className="w-14 text-right text-[11px] font-mono" style={{ color: '#0D1B2A' }}>{loc.count.toLocaleString()}</span>
              </div>
            );
          })}
        </Card>

        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="industry-dist">
          <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#0D1B2A' }}>Industry Distribution</h3>
          {(data?.industry_distribution || []).slice(0, 10).map((ind, i) => {
            const maxCount = (data?.industry_distribution?.[0]?.count) || 1;
            return (
              <div key={ind.industry} className="flex items-center gap-2 py-1">
                <span className="w-52 text-xs truncate" style={{ color: '#0D1B2A' }}>{ind.industry}</span>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.1)' }}>
                  <div className="h-full rounded-full" style={{ width: `${(ind.count/maxCount)*100}%`, background: '#1A4C7C' }} />
                </div>
                <span className="w-14 text-right text-[11px] font-mono" style={{ color: '#0D1B2A' }}>{ind.count.toLocaleString()}</span>
              </div>
            );
          })}
        </Card>
      </div>

      <ValidationSection api={api} />
    </div>
  );
}
